import PropTypes from "prop-types";
import React from "react";
import { Mutation } from "react-apollo";

import {
  CREATE_SUBSTORYSECTION,
  DELETE_SUBSTORYSECTION,
  GET_STORIES,
  UPDATE_SUBSTORYSECTION,
} from "./../../gql";

import ContentPanel from "./../../components/ContentPanel";
import ErrorBoundary from "./../../components/ErrorBoundary";
import Tags from "./../../components/Tags";
import Throbber from "./../../components/Throbber";
import VisibilityToggle from "./../../components/VisibilityToggle";
import WysiwygEditor from "./../../components/WysiwygEditor";

import roundPlus from "./../../../images/icons/round-plus-white.svg";

class SubStorySections extends React.Component {
  constructor() {
    super();
    this.state = {
      storySubSectionUpdate: {},
      publishIsUpToDate: null,
    };
    this.submitDelay = 500;
  }

  componentDidUpdate(prevProps) {
    const { published } = this.props;

    if (!published && prevProps.published) {
      this.setState({
        publishIsUpToDate: false,
      });
    }

    if (published && !prevProps.published) {
      this.setState({
        publishIsUpToDate: true,
      });
    }
  }

  createStorySubSection(createStorySubSection, subStorySections) {
    const {
      storySectionId,
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    const orderIndex =
      subStorySections.length > 0
        ? subStorySections[subStorySections.length - 1].meta.displayOrder + 1
        : 0;

    createStorySubSection({
      variables: {
        title: "",
        subtitle: "",
        storySectionId: storySectionId,
        languageCode: languageCode,
        app: app,
        content: {
          document: {
            nodes: [
              {
                object: "block",
                type: "paragraph",
              },
            ],
          },
        },
        hiddenFromDashboard: false,
        shownRandomly: true,
        displayOrder: orderIndex,
      },
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  deleteStorySubSection(id, deleteStorySubSection) {
    const { activatePublishButton, activateUnpublishedAlert } = this.props;
    if (confirm("Are you sure you want to delete this storySubSection?")) {
      deleteStorySubSection({
        variables: { id },
      });
      activatePublishButton();
      activateUnpublishedAlert();
    }
  }

  submitUpdate(updateStorySubSection) {
    const { activatePublishButton, activateUnpublishedAlert } = this.props;
    const { storySubSectionUpdate } = this.state;
    updateStorySubSection({
      variables: storySubSectionUpdate,
    });
    this.setState({
      storySubSectionUpdate: {},
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onIsBornChange(id, value, updateFunc) {
    const {
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;
    const isBorn = value ? false : true;

    updateFunc({
      variables: {
        app,
        languageCode,
        id,
        isBorn,
        published: false,
      },
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onChangeHandle(event, id, updateStorySubSection) {
    const { app, languageCode, published } = this.props;
    const name = event.target.name;
    const value = isNaN(event.target.value)
      ? event.target.value
      : Number(event.target.value);

    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(
      () => this.submitUpdate(updateStorySubSection),
      this.submitDelay
    );

    this.setState((prevState) => {
      if (
        prevState.storySubSectionUpdate &&
        prevState.storySubSectionUpdate.id === id
      ) {
        return {
          storySubSectionUpdate: {
            ...this.state.storySubSectionUpdate,
            id: id,
            [name]: value,
          },
        };
      }
      return {
        storySubSectionUpdate: {
          id: id,
          app: app,
          published: published,
          languageCode: languageCode,
          [name]: value,
        },
      };
    });
  }

  onChangeEditor(value, id, updateStorySubSection) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    clearTimeout(this.editorTimer);
    this.editorTimer = setTimeout(() => {
      updateStorySubSection({
        variables: {
          id: id,
          app: app,
          published: published,
          languageCode: languageCode,
          content: value,
        },
      });
      activatePublishButton();
      activateUnpublishedAlert();
    }, this.submitDelay);
  }

  onVisibilityChange(event, id, updateStorySubSection) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;
    updateStorySubSection({
      variables: {
        id: id,
        app: app,
        published: published,
        languageCode: languageCode,
        hiddenFromDashboard: event === "hiddenFromDashboard" ? true : false,
        shownRandomly: event === "hiddenFromDashboard" ? false : true,
      },
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onTagsChange(tags, id, updateStorySubSection) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    updateStorySubSection({
      variables: {
        id: id,
        app: app,
        published: published,
        languageCode: languageCode,
        tags: tags,
      },
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  updateDisplayOrder(id, order, updateFunc) {
    const {
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    updateFunc({
      variables: {
        id,
        app,
        languageCode,
        published: false,
        displayOrder: order,
      },
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  publishNotification() {
    const { activatePublishButton } = this.props;
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      activatePublishButton();
    } else if (publishIsUpToDate) {
      return;
    }

    return (
      <div className="notification notification--warning">
        <p className="notification__description">
          The content below has not been published yet.
        </p>
      </div>
    );
  }

  render() {
    const { subStorySections } = this.props;
    const { app, languageCode, published } = this.props;

    return (
      <div>
        {subStorySections
          .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
          .map((storySubSection) => (
            <Mutation
              key={storySubSection.id}
              mutation={DELETE_SUBSTORYSECTION}
              refetchQueries={[
                {
                  query: GET_STORIES,
                  variables: {
                    languageCode: languageCode,
                    app: app,
                  },
                },
              ]}
            >
              {(deleteStorySubSection, { loading, error }) => (
                <div className="relative-block">
                  {error && (
                    <p className="full-width center-align">{error.message}</p>
                  )}
                  {!storySubSection.availability.published &&
                    this.publishNotification()}
                  <Mutation
                    mutation={UPDATE_SUBSTORYSECTION}
                    refetchQueries={[
                      {
                        query: GET_STORIES,
                        variables: {
                          languageCode: languageCode,
                          app: app,
                        },
                      },
                    ]}
                  >
                    {(updateStorySubSection) => (
                      <ContentPanel
                        key={storySubSection.id}
                        enableWeekRange
                        enableOrdering
                        id={storySubSection.id}
                        title={storySubSection.title}
                        isBorn={storySubSection.availability.isBorn}
                        rangeFrom={storySubSection.availability.fromWeek}
                        rangeTo={storySubSection.availability.toWeek}
                        displayOrder={storySubSection.meta.displayOrder}
                        siblingPanels={subStorySections}
                        onIsBornChange={(id, value) =>
                          this.onIsBornChange(id, value, updateStorySubSection)
                        }
                        updateDisplayOrder={(id, order) =>
                          this.updateDisplayOrder(
                            id,
                            order,
                            updateStorySubSection
                          )
                        }
                        onChanges={(event) =>
                          this.onChangeHandle(
                            event,
                            storySubSection.id,
                            updateStorySubSection
                          )
                        }
                        deletePanel={() =>
                          this.deleteStorySubSection(
                            storySubSection.id,
                            deleteStorySubSection
                          )
                        }
                      >
                        <ErrorBoundary>
                          <WysiwygEditor
                            enableArticleLinking
                            enableExternalLinking
                            enableImageUpload
                            enableAudioUpload
                            enableVimeoEmbed
                            enableEmbeds
                            enableIgniteVideo
                            languageCode={languageCode}
                            app={app}
                            published={published}
                            editorData={storySubSection.content}
                            onChangeValue={(event) =>
                              this.onChangeEditor(
                                event,
                                storySubSection.id,
                                updateStorySubSection
                              )
                            }
                          />
                        </ErrorBoundary>
                        <div className="content-panel__footer">
                          <div className="content-panel__form">
                            <Tags
                              tags={storySubSection.tags}
                              onChange={(event) =>
                                this.onTagsChange(
                                  event,
                                  storySubSection.id,
                                  updateStorySubSection
                                )
                              }
                            />
                          </div>
                          <div className="content-panel__actions">
                            <VisibilityToggle
                              parentId={storySubSection.id}
                              onChange={(event) =>
                                this.onVisibilityChange(
                                  event,
                                  storySubSection.id,
                                  updateStorySubSection
                                )
                              }
                              options={[
                                {
                                  id: "shownRandomly",
                                  checked:
                                    storySubSection.availability.shownRandomly,
                                },
                                {
                                  id: "hiddenFromDashboard",
                                  checked:
                                    storySubSection.availability
                                      .hiddenFromDashboard,
                                },
                              ]}
                            />
                          </div>
                        </div>
                      </ContentPanel>
                    )}
                  </Mutation>
                  <Throbber
                    hidden={!loading}
                    loadingText="Deleting section..."
                  />
                </div>
              )}
            </Mutation>
          ))}
        <Mutation
          mutation={CREATE_SUBSTORYSECTION}
          refetchQueries={[
            {
              query: GET_STORIES,
              variables: {
                languageCode: languageCode,
                app: app,
              },
            },
          ]}
        >
          {(createStorySubSection, { loading, error }) => (
            <div className="full-width right-align relative-block">
              <Throbber
                hidden={!loading}
                loadingText="Creating storySubSection..."
              />
              {error && (
                <p className="full-width center-align">{error.message}</p>
              )}
              <div className="full-width right-align">
                <button
                  className="button button--small button--icon-text"
                  disabled={loading}
                  onClick={() =>
                    this.createStorySubSection(
                      createStorySubSection,
                      subStorySections
                    )
                  }
                >
                  <img
                    src={roundPlus}
                    alt="Add new storySubSection"
                    className="button__icon"
                  />
                  Add new storySubSection
                </button>
              </div>
            </div>
          )}
        </Mutation>
      </div>
    );
  }
}

SubStorySections.propTypes = {
  storySectionId: PropTypes.string.isRequired,
  subStorySections: PropTypes.array.isRequired,
  languageCode: PropTypes.string.isRequired,
  published: PropTypes.bool.isRequired,
  app: PropTypes.string.isRequired,
  activatePublishButton: PropTypes.func.isRequired,
  activateUnpublishedAlert: PropTypes.func.isRequired,
};

export default SubStorySections;
